import { isCommandResponse, isCommandsResponse } from 'core/api/api.typesGuards';
import * as I from 'core/api/coreApiClient';
import DropDown from 'core/components/Elements/DropDown/DropDown';
import { getCommand } from 'core/redux/actions/command/commandActions';
import type { AppThunkDispatch } from 'core/redux/store/configureStore.types';
import { isCommandForReactRouter } from 'core/utilities/command/command';
import type { EnhancedCommand } from 'core/utilities/core.types';
import React from 'react';
import { useDispatch } from 'react-redux';

import CommandButton from './CommandButton/CommandButton';
import CommandDelete from './CommandDelete/CommandDelete';
import CommandExternalLink from './CommandExternalLink/CommandExternalLink';
import CommandFetchDisabled from './CommandFetchDisabled/CommandFetchDisabled';
import CommandFileDownload from './CommandFileDownload/CommandFileDownload';
import CommandGet from './CommandGet/CommandGet';
import CommandLink from './CommandLink/CommandLink';
import CommandLinkContent from './CommandLinkContent/CommandLinkContent';
import CommandOnClick from './CommandOnClick/CommandOnClick';
import CommandSave from './CommandSave/CommandSave';
import CommandToggleButton from './CommandToggleButton/CommandToggleButton';
import StyleHintIcon from './StyleHintIcon/StyleHintIcon';

export interface ComponentProps {
  command: EnhancedCommand;
  replaceCommand?: (command: EnhancedCommand, newCommands: EnhancedCommand[]) => void;
}

export const Command = ({ command: inputCommand, replaceCommand }: ComponentProps) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [command, setCommand] = React.useState<EnhancedCommand>(inputCommand);

  // Fetch command data only once
  React.useEffect(() => {
    const { fetchUrl } = command;

    if (fetchUrl) {
      const fetchHandledCommand: EnhancedCommand = { ...command, fetchUrl: undefined };

      dispatch(getCommand({ ...command, url: fetchUrl }, true)).then((response) => {
        if (isCommandResponse(response) && response.payload) {
          if (replaceCommand) {
            // Higher level method replaceCommand will delete that command and replace it by the response
            replaceCommand(fetchHandledCommand, [response.payload]);
          } else {
            setCommand(response.payload);
          }
        } else if (isCommandsResponse(response) && response.payload && replaceCommand) {
          replaceCommand(fetchHandledCommand, response.payload.commands);
        } else if (replaceCommand) {
          replaceCommand(fetchHandledCommand, [{ ...fetchHandledCommand, active: false }]);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setCommand(inputCommand);
  }, [inputCommand]);

  const render = () => {
    const {
      id, active, httpVerb, commands, commandType, icon,
      small, inheritColor, iconEl, label, title, styleHint, fetchUrl, hideArrowIcon, toggleButton,
    } = command;

    if (fetchUrl && httpVerb === I.HttpVerbConstants.GET) {
      return (<CommandFetchDisabled command={command} small={small} />);
    }

    if (commandType === I.CommandTypeConstants.FileDownload) {
      return (<CommandFileDownload command={command} small={small} />);
    }

    if (commandType === I.CommandTypeConstants.RESTResource) {
      // Example: typically save buttons
      if (httpVerb === I.HttpVerbConstants.PUT) {
        return (<CommandSave command={command} />);
      }
      // Example: requires confirm
      if (httpVerb === I.HttpVerbConstants.GET) {
        return (<CommandGet command={command} small={small} />);
      }
      // Example: delete entity
      if (httpVerb === I.HttpVerbConstants.DELETE) {
        return (<CommandDelete command={command} small={small} />);
      }
    }

    // ExternalHREF, JSFHREF
    if (!isCommandForReactRouter(command)) {
      return (<CommandExternalLink command={command} small={small} />);
    }

    if (!commandType && toggleButton) {
      return (<CommandToggleButton command={command} />);
    }

    if (!commandType && commands.length > 0) {
      const shIcon = StyleHintIcon(styleHint);

      if (shIcon) {
        return (
          <DropDown
            id={id}
            action="tertiary"
            title={title || label}
            items={commands}
            buttonSize={small ? 'small' : 'medium'}
            inheritColor={inheritColor}
            disabled={active === false}
            arrowIcon={shIcon}
          />
        );
      }
      return (
        <DropDown
          id={id}
          action="tertiary"
          label={label || iconEl || icon ? <CommandLinkContent command={command} /> : undefined}
          title={title}
          items={commands}
          buttonSize={small ? 'small' : 'medium'}
          inheritColor={inheritColor}
          disabled={active === false}
          hideArrowIcon={hideArrowIcon || icon !== undefined}
        />
      );
    }

    // Special case: this command is never returned by the API
    // But is used hardcoded within the app (example logout link within Header)
    // Ideally this should be converted to CommandButton, so we dispatch command
    // and middleware acts accordingly
    if (command.onClick) {
      return (<CommandOnClick command={command} small={small} />);
    }

    // Example: link to another page
    if (commandType === I.CommandTypeConstants.HREF) {
      return (<CommandLink command={command} small={small} />);
    }

    // If the command is not rendered above, render it as a button
    // that will dispatch the command on click
    return (<CommandButton command={command} small={small} />);
  };

  return render();
};

export default Command;
